<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { Done } from '@xpos/icons-svg';
import packageConfig from '@/../package.json';
import { InstallMode, codePush } from 'cap-codepush';
import { useI18n } from 'vue-i18n';
import { SyncStatus } from 'cap-codepush/dist/esm/syncStatus';
import type { DownloadProgress } from 'cap-codepush/dist/esm/package';
import { useUpdateStore, type UpdateInfo } from '@/stores/update';
import { logInfo, logError } from '@/utils/logger';

const { t: $t } = useI18n();
const $emit = defineEmits(['change', 'update:modelValue', 'close']);
const $props = withDefaults(defineProps<{
  isForce?: boolean;
  modelValue?: boolean;
  upgradeInfo?: UpdateInfo;
  autoUpdate?: boolean;
}>(), {
  isForce: false,
  modelValue: false,
  upgradeInfo: () => ({ description: '', version: '' }),
  autoUpdate: false,
});

// Watch for modelValue changes to control dialog visibility
watch(() => $props.modelValue, (newVal) => {
  visible.value = newVal;

  // Automatically start the update process when dialog is shown and autoUpdate is true
  if (newVal && $props.autoUpdate && updrageStatus.value === 0) {
    handleConfirm();
  }
});

const visible = ref($props.modelValue);
const updateStore = useUpdateStore();

const title = ref($t('upgrade.title'));
const updrageStatus = ref(0);
const percentage = ref(0);
const color = ref('#52599D');

function handleClose(): void {
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close', false);
  visible.value = false;
}

function codePushSync(): void {
  codePush
    .sync(
      {
        installMode: InstallMode.IMMEDIATE,
        onSyncError: (error: unknown) => {
          logError('CodePush sync error', { error: String(error) });
          updrageStatus.value = 2;
          color.value = '#E53030';
        },
      },
      onDownloadProgress,
    )
    .then((status) => {
      logInfo('CodePush sync status', { status });
      switch (status) {
        case SyncStatus.DOWNLOADING_PACKAGE:
          break;
        case SyncStatus.ERROR:
          logError('CodePush sync status error', { version: $props.upgradeInfo?.version });
          title.value = $t('upgrade.updateFailed');
          updrageStatus.value = 2;
          color.value = '#E53030';
          break;
        case SyncStatus.INSTALLING_UPDATE:
          logInfo('CodePush update installed', { version: $props.upgradeInfo?.version });
          title.value = $t('upgrade.updateComplete');
          updrageStatus.value = 3;
          color.value = '#80BA5C';
          updateStore.clearUpdate();
          break;
      }
    })
    .catch((error: unknown) => {
      // Handle unhandled promise rejections (e.g., empty archive file, network issues)
      logError('CodePush sync failed', { error: String(error) });
      title.value = $t('upgrade.updateFailed');
      updrageStatus.value = 2;
      color.value = '#E53030';
    });
}
function onDownloadProgress(progress?: DownloadProgress): void {
  if (!progress) return;
  percentage.value = Math.round((progress.receivedBytes / progress.totalBytes) * 100);
}

function handleConfirm(): void {
  logInfo('CodePush update started', { version: $props.upgradeInfo?.version, from: packageConfig.version, auto: $props.autoUpdate });
  title.value = $t('upgrade.upgrading');
  updrageStatus.value = 1;
  codePushSync();
}

// Progress label text/color based on upgrade status
const progressLabel = computed(() => {
  if (updrageStatus.value === 1) return $t('upgrade.status.upgrading')
  if (updrageStatus.value === 2) return $t('upgrade.status.error')
  return $t('upgrade.status.complete')
})
const progressLabelColor = computed(() => {
  if (updrageStatus.value === 3) return '#80BA5C'
  if (updrageStatus.value === 2) return '#3a3a3a'
  return undefined
})
</script>
<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="title"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
    :hidden-close-button="true"
  >
    <div class="w-[355px] flex flex-col justify-between" v-if="updrageStatus == 0">
      <div>
        <div class="flex w-full justify-center my-[50px]">
          <img src="/updrage.png" alt="" class="w-[100px] h-[100x]" />
        </div>
        <div class="text-xl text-primary text-center mb-5 font-medium">
          {{ $t('upgrade.updateVer') }} {{ $props.upgradeInfo?.version }}
        </div>
        <div class="text-center">
          {{ $t('upgrade.aboutUpdate') }}:<br />
          <pre>{{ $props.upgradeInfo.description }}</pre>
        </div>
      </div>
      <div>
        <div class="text-right mb-2 text-[#3a3a3a]">{{ $t('upgrade.currentVer') }}:{{ packageConfig.version }}</div>
        <el-button color="#80BA5C" class="w-full text-white mb-2" @click="handleConfirm"
          ><el-icon size="20" color="#fff" class=""><Done /></el-icon
          ><span class="text-white align-super">{{ $t('upgrade.update') }}</span></el-button
        >
        <div>
          <el-button type="primary" class="w-full" @click="handleClose" v-if="!$props.isForce">{{
            $t('upgrade.updateLater')
          }}</el-button>
        </div>
      </div>
    </div>
    <!-- Progress view: upgrading (1), error (2), complete (3) -->
    <div class="w-[355px] flex flex-col justify-between" v-else>
      <div>
        <div class="flex w-full justify-center my-[50px] mt-[100px]">
          <el-progress type="dashboard" :percentage="percentage" :color="color" :stroke-width="8">
            <template #default="{ percentage }">
              <div class="percentage-value text-[40px]">{{ percentage }}%</div>
              <span class="percentage-label text-sm" :style="{ color: progressLabelColor }">{{ progressLabel }}</span>
            </template></el-progress
          >
        </div>
        <div class="text-center text-xl text-[#3a3a3a]" v-if="updrageStatus == 1">{{ $t('upgrade.tips') }}</div>
        <div class="text-center text-xl text-[#3a3a3a]" v-else-if="updrageStatus == 2">{{ $t('upgrade.updateFailedTips') }}</div>
        <div class="text-center text-xl text-[#3a3a3a]" v-else>{{ $t('upgrade.completed') }} {{ $props.upgradeInfo?.version }}</div>
      </div>
      <el-button v-if="updrageStatus >= 2" color="#80BA5C" class="w-full text-white mb-2" @click="handleClose"
        ><el-icon size="20" color="#fff" class=""><Done /></el-icon
        ><span class="text-white align-super">{{ $t('upgrade.ok') }}</span></el-button
      >
    </div>
  </x-dialog>
</template>
